/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { typography } from '../../shared/theme/typography';
import { colors } from '../../shared/theme/colors';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterAvailabilityRow, OperationalFilterFieldLabel, OperationalFilterPickerRow, OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';
import type { CatalogFilter } from '../domain/catalogTypes';
import type { CatalogItemTypeFilter } from '../domain/productTypes';
import type { CategoryFilterOption } from '../domain/categoryOptions';
import { statusFilters } from '../domain/catalogFilterOptions';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';

const BASE_ITEM_TYPE_OPTIONS: Array<{ value: CatalogItemTypeFilter; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'physical', label: 'Productos' },
  { value: 'service', label: 'Servicios' },
  { value: 'combo', label: 'Combos' },
];

const STATUS_OPTIONS: Array<{ value: CatalogFilter; label: string }> = [
  { value: 'all', label: 'Todos' },
  ...statusFilters.map((s) => ({ value: s.id, label: s.label })),
];

type SheetKey = 'category' | 'brand' | 'supplier' | null;

export type CatalogFilterDraft = {
  itemTypeFilter: CatalogItemTypeFilter;
  statusFilter: CatalogFilter;
  categoryFilter: string | null | 'all';
  brandFilter: string | null;
  supplierFilter: string | null;
  alertFilter: boolean;
  incompleteFilter: boolean;
};

export type CatalogFilterSheetContentProps = {
  draft: CatalogFilterDraft;
  setDraft: (updater: (prev: CatalogFilterDraft) => CatalogFilterDraft) => void;
  categoryFilters: CategoryFilterOption[];
  getCategoryLabel: (slug?: string | null) => string;
  brandOptions: Array<{ id: string; name: string }>;
  supplierOptions: Array<{ id: string; name: string }>;
  alertsOnPage: number;
  incompleteOnPage: number;
  showStockAlertFilter?: boolean;
};

export function CatalogFilterSheetContent({
  draft,
  setDraft,
  categoryFilters,
  getCategoryLabel,
  brandOptions,
  supplierOptions,
  alertsOnPage,
  incompleteOnPage,
  showStockAlertFilter = true,
}: CatalogFilterSheetContentProps) {
  const [openSheet, setOpenSheet] = useState<SheetKey>(null);
  const productCombosEnabled = useHasBusinessCapability('product_combos');
  const recipesEnabled = useHasBusinessCapability('recipes');
  const itemTypeOptions = useMemo(() => {
    const opts = [...BASE_ITEM_TYPE_OPTIONS];
    if (!productCombosEnabled) {
      return opts.filter((o) => o.value !== 'combo');
    }
    if (recipesEnabled) {
      opts.push({ value: 'recipe', label: 'Con receta' });
    }
    return opts;
  }, [productCombosEnabled, recipesEnabled]);

  const categoryLabel = useMemo(() => {
    if (draft.categoryFilter === 'all' || !draft.categoryFilter) return 'Todas';
    const opt = categoryFilters.find((c) => c.id === draft.categoryFilter);
    return opt?.label ?? getCategoryLabel(draft.categoryFilter);
  }, [draft.categoryFilter, categoryFilters, getCategoryLabel]);

  const brandLabel = useMemo(() => {
    if (!draft.brandFilter) return 'Cualquiera';
    return brandOptions.find((b) => b.id === draft.brandFilter)?.name ?? 'Cualquiera';
  }, [draft.brandFilter, brandOptions]);

  const supplierLabel = useMemo(() => {
    if (!draft.supplierFilter) return 'Cualquiera';
    return supplierOptions.find((s) => s.id === draft.supplierFilter)?.name ?? 'Cualquiera';
  }, [draft.supplierFilter, supplierOptions]);

  const categorySheetOptions = useMemo(
    () =>
      categoryFilters
        .filter((c) => {
          const id = String(c.id).toLowerCase();
          const label = String(c.label).toLowerCase();
          return id !== 'all' && label !== 'todas' && label !== 'todos';
        })
        .map((c) => ({ id: c.id, name: c.label })),
    [categoryFilters]
  );

  const brandSheetOptions = useMemo(
    () => brandOptions.map((b) => ({ id: b.id, name: b.name })),
    [brandOptions]
  );

  const supplierSheetOptions = useMemo(
    () => supplierOptions.map((s) => ({ id: s.id, name: s.name })),
    [supplierOptions]
  );

  return (
    <View style={styles.container}>
      <View style={styles.section}>
        <OperationalFilterFieldLabel>Tipo</OperationalFilterFieldLabel>
        <OperationalFilterSegmentedRow
          options={itemTypeOptions}
          value={draft.itemTypeFilter}
          onChange={(v) => setDraft((p) => ({ ...p, itemTypeFilter: v }))}
          ticketTone
        />
        {productCombosEnabled ? (
          <Text style={styles.typeHint}>
            {recipesEnabled
              ? 'Productos: sin combos. Combos y receta van aparte.'
              : 'Productos: sin combos.'}
          </Text>
        ) : null}
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Estado</OperationalFilterFieldLabel>
        <OperationalFilterSegmentedRow
          options={STATUS_OPTIONS}
          value={draft.statusFilter}
          onChange={(v) => setDraft((p) => ({ ...p, statusFilter: v }))}
          ticketTone
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Clasificación</OperationalFilterFieldLabel>
        <OperationalFilterPickerRow
          layout="stack"
          label="Categoría"
          valueLabel={categoryLabel}
          active={draft.categoryFilter !== 'all' && draft.categoryFilter != null}
          onPress={() => setOpenSheet('category')}
        />
        {brandOptions.length > 0 ? (
          <OperationalFilterPickerRow
            layout="stack"
            label="Marca"
            valueLabel={brandLabel}
            active={!!draft.brandFilter}
            onPress={() => setOpenSheet('brand')}
          />
        ) : null}
        {supplierOptions.length > 0 ? (
          <OperationalFilterPickerRow
            layout="stack"
            label="Proveedor"
            valueLabel={supplierLabel}
            active={!!draft.supplierFilter}
            onPress={() => setOpenSheet('supplier')}
          />
        ) : null}
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Señales</OperationalFilterFieldLabel>
        {showStockAlertFilter ? (
          <OperationalFilterAvailabilityRow
            title="Alertas"
            hint={
              alertsOnPage > 0
                ? `Sin stock, bajo mínimo o sin costo · ${alertsOnPage} en catálogo`
                : 'Sin stock, bajo mínimo o sin costo'
            }
            selected={draft.alertFilter}
            onPress={() => setDraft((p) => ({ ...p, alertFilter: !p.alertFilter }))}
          />
        ) : null}
        <OperationalFilterAvailabilityRow
          title="Incompletos"
          hint={
            incompleteOnPage > 0
              ? `Sin código, categoría o costo · ${incompleteOnPage} acá`
              : 'Sin código, categoría o costo'
          }
          selected={draft.incompleteFilter}
          onPress={() => setDraft((p) => ({ ...p, incompleteFilter: !p.incompleteFilter }))}
        />
      </View>

      <MasterFilterSheetModal
        visible={openSheet === 'category'}
        title="Categoría"
        options={categorySheetOptions}
        selectedId={draft.categoryFilter === 'all' || !draft.categoryFilter ? null : draft.categoryFilter}
        allOptionLabel="Todas"
        appearance="ticket"
        onSelect={(id: string | null) =>
          setDraft((p) => ({ ...p, categoryFilter: (id ?? 'all') as string | 'all' }))
        }
        onClose={() => setOpenSheet(null)}
      />
      <MasterFilterSheetModal
        visible={openSheet === 'brand'}
        title="Marca"
        options={brandSheetOptions}
        selectedId={draft.brandFilter}
        allOptionLabel="Cualquiera"
        appearance="ticket"
        onSelect={(id: string | null) => setDraft((p) => ({ ...p, brandFilter: id }))}
        onClose={() => setOpenSheet(null)}
      />
      <MasterFilterSheetModal
        visible={openSheet === 'supplier'}
        title="Proveedor"
        options={supplierSheetOptions}
        selectedId={draft.supplierFilter}
        allOptionLabel="Cualquiera"
        appearance="ticket"
        onSelect={(id: string | null) => setDraft((p) => ({ ...p, supplierFilter: id }))}
        onClose={() => setOpenSheet(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 18,
  },
  section: {
    gap: 8,
  },
  typeHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 4,
  },
});
